import { Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import CheckItemsList from "@entrilia/shared/components/inputs/CheckItemsList";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { AccessLevelEntity } from "../../../../../api/types/userManagementTypes";
import MembersContext from "../MembersContext";
import InviteMembersContext from "./InviteMembersContext";

interface Props {
  clientCode: string;
  selectedEntityIds: string[];
  onSelectedEntityIdsChange: (selectedEntityIds: string[], accessToAllEntities: boolean) => void;
  accessToAllEntities: boolean;
}

const toListItem = (entity: AccessLevelEntity): ListItemData => ({
  value: entity.id,
  label: entity.name || (entity.no ?? entity.id),
});

const EntitiesAccess = ({ selectedEntityIds, clientCode, onSelectedEntityIdsChange, accessToAllEntities }: Props) => {
  const { companies } = useDefinedContext(MembersContext);
  const { getCompanyEntities, entitiesFetchError } = useDefinedContext(InviteMembersContext);

  const [search, setSearch] = useState<string>("");

  const handleSelectedEntitiesChanged = useCallback(
    (selectedEntities: ListItemData[]) => {
      onSelectedEntityIdsChange(
        selectedEntities.map((entity) => entity.value),
        accessToAllEntities
      );
    },
    [accessToAllEntities, onSelectedEntityIdsChange]
  );

  const handleAccessToAllEntitiesChange = (checked: boolean) => {
    onSelectedEntityIdsChange([], checked);
  };

  const company = companies.find((c) => c.clientCode === clientCode);
  const entities = getCompanyEntities(clientCode);

  const entitiesList: ListItemData[] = (entities ?? [])
    .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
    .map(toListItem)
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedEntitiesList: ListItemData[] = (entities ?? [])
    .filter((entity) => selectedEntityIds.includes(entity.id))
    .map(toListItem);

  return (
    <Stack spacing={1} sx={{ maxHeight: "90%" }}>
      <Typography variant="subtitle1">Entities</Typography>
      <Typography color="textSecondary">{company?.title || clientCode}</Typography>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              sx={{ ml: 1.3 }}
              size="medium"
              onChange={(_e, checked) => handleAccessToAllEntitiesChange(checked)}
              checked={accessToAllEntities}
            />
          }
          label={<Typography>Access to all entities</Typography>}
        />
      </FormGroup>

      {!accessToAllEntities && entities === undefined && entitiesFetchError && (
        <DataLoadingFailed title="Entities could not be loaded" text="Please try again later or contact support." />
      )}
      {!accessToAllEntities && entities === undefined && !entitiesFetchError && <InlineLoader />}
      {!accessToAllEntities && entities && (
        <>
          <SearchField initialValue={search} onSearch={setSearch} debounceTimeMs={300} />
          <CheckItemsList
            disablePadding
            items={entitiesList}
            onSelectedChanged={handleSelectedEntitiesChanged}
            preselectedItems={selectedEntitiesList}
          />
        </>
      )}
    </Stack>
  );
};

export default EntitiesAccess;
